import { Button, Card, Col, DatePicker, Flex, Form, Input, Row, Spin } from 'antd'
import { nbrStatApi } from 'common/api/entitiesApi.tsx'
import { AnchorsForm } from 'common/components/AnchorsForm.tsx'
import { FormCard } from 'common/components/FormCard.tsx'
import { AdGroupSelector } from 'common/components/reloadable/AdGroupSelector.tsx'
import { DspSelector } from 'common/components/reloadable/DspSelector.tsx'
import { SspSelector } from 'common/components/reloadable/SspSelector.tsx'
import { useAppContext } from 'common/context/AppContext.tsx'
import { apiCall } from 'common/util/Util.tsx'
import dayjs from 'dayjs'
import { Moment } from 'moment'
import React, { useState } from 'react'

export interface NbrDebugProps {
  nbrType?: 'adGroup' | 'dsp'
}

interface FormValues {
  outerSsp?: string
  sspId?: number
  adGroupId?: number
  dspId?: number
  date: Moment
}

const NbrDebug: React.FC<NbrDebugProps> = ({ nbrType = 'adGroup' }) => {
  const appContext = useAppContext()
  const [form] = Form.useForm<FormValues>()

  const [result, setResult] = useState<any>(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  const handleSubmit = () => {
    setLoading(true)
    form.validateFields().then((formData) => {
      const dateStr = formData.date.format('YYYY-MM-DD')

      const requestFn =
        nbrType === 'adGroup'
          ? () => nbrStatApi.adGroupNbr(formData.adGroupId || 0, dateStr, formData.sspId, formData.outerSsp)
          : () => nbrStatApi.dspNbr(formData.dspId || 0, dateStr, formData.sspId, formData.outerSsp)

      apiCall(setLoading, appContext, requestFn, (response) => setResult(response))
    })
  }

  return (
    <Spin spinning={loading}>
      <AnchorsForm form={form} onFinish={handleSubmit}>
        <FormCard id="parameters" title={`${nbrType === 'adGroup' ? 'AdGroup' : 'DSP'} NBR Debug`}>
          <Form.Item label="Outer SSP" name="outerSsp">
            <Input allowClear placeholder="Input Outer SSP" />
          </Form.Item>

          <Form.Item label="SSP" name="sspId">
            <SspSelector placeholder="Select SSP" />
          </Form.Item>

          {nbrType === 'adGroup' ? (
            <Form.Item label="AdGroup" name="adGroupId" rules={[{ required: true, message: 'Select AdGroup' }]}>
              <AdGroupSelector placeholder="Filter by Ad Group" />
            </Form.Item>
          ) : (
            <Form.Item label="DSP" name="dspId" rules={[{ required: true, message: 'Select DSP' }]}>
              <DspSelector placeholder="Select DSP" />
            </Form.Item>
          )}

          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: 'Select date' }]}
            initialValue={dayjs()}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
            />
          </Form.Item>
        </FormCard>

        <Row>
          <Col span={4} />
          <Col span={20}>
            <Flex style={{ marginTop: 20, marginBottom: 30, marginRight: 24, marginLeft: 16 }}>
              <Flex gap="middle" justify="flex-start" align="flex-start">
                <Button type="primary" className="adsp-button" htmlType="submit" disabled={loading}>
                  Send Request
                </Button>
              </Flex>
            </Flex>
          </Col>
        </Row>

        <FormCard id="result" title="Result">
          {result ? (
            <Card type="inner" styles={{ body: { padding: 0 } }} style={{ border: 'none', boxShadow: 'none' }}>
              <pre
                style={{
                  background: '#f7f7f7',
                  padding: 12,
                  borderRadius: 4,
                  maxHeight: 600,
                  overflow: 'auto',
                  margin: 0,
                }}
              >
                {JSON.stringify(result, null, 2)}
              </pre>
            </Card>
          ) : (
            <Card type="inner" style={{ border: 'none', boxShadow: 'none' }}>
              <Card.Meta title="Waiting for result" description="Submit the parameters to see the response here." />
            </Card>
          )}
        </FormCard>
      </AnchorsForm>
    </Spin>
  )
}

export default NbrDebug
